import { useState } from 'react';
import { Phone, Calendar, Clock, Zap, Users, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import VoiceCallWidget from '../../../components/VoiceCallWidget';
import ContioLeadCaptureModal from '../../../components/ContioLeadCaptureModal';

export default function ContioAILanding() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <ContioLeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        landingPageId="contioai"
      />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-[#45B787]/10 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#45B787] mr-2" />
              <span className="text-sm font-medium text-[#45B787]">
                Exclusively for the ContioAI Team
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Hey Aaron, 👋
              <br />
              <span className="bg-gradient-to-r from-[#45B787] to-[#FFD23F] bg-clip-text text-transparent">
                37,000 meetings taught you this: Only the transformative ones matter.
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-4 max-w-3xl mx-auto leading-relaxed">
              ContioAI makes your strategic meetings 10x better. We eliminate the other 70%:
              <span className="font-semibold text-gray-900"> the update calls, check-ins, and "quick questions" stealing 23 hours of your week</span>.
            </p>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              DoubleMe is your AI-powered phone assistant that handles calls for you.
              While you're building Contio or making it to your kids' soccer games,
              your AI double is taking calls, scheduling follow-ups, and keeping your pipeline moving.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center px-8 py-4 bg-[#45B787] text-white rounded-lg font-semibold hover:bg-[#3a9970] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Get on the Beta List
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <a
                href="#how-it-works"
                className="inline-flex items-center px-8 py-4 bg-white text-[#45B787] border-2 border-[#45B787] rounded-lg font-semibold hover:bg-[#45B787]/5 transition-all"
              >
                See How It Works
              </a>
            </div>

            {/* Social Proof */}
            <div className="inline-flex items-center text-sm text-gray-500">
              <CheckCircle className="w-4 h-4 text-[#45B787] mr-2" />
              <span>Built for busy founders who value freedom over busyness</span>
            </div>
          </div>
        </div>
      </div>

      {/* Live Demo Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Try It Right Now
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We trained a DoubleMe agent specifically on ContioAI. Test it—ask about your company's mission, your team's challenges, or how we'd customize this for you.
            </p>
          </div>

          <VoiceCallWidget agentId="agent_1801kaq4gz17fbkbrkz4kcrvep29" />

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              Or call directly: <a href="tel:+16156901805" className="font-semibold text-[#45B787] hover:text-[#3a9970]">+1 (615) 690-1805</a>
            </p>
            <p className="text-xs text-gray-500">
              This demo agent is available 24/7. Try asking about pricing, features, or how DoubleMe works with ContioAI.
            </p>
          </div>
        </div>
      </div>

      {/* The Problem - Speaking Aaron's Language */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              You Already Know the Problem
            </h2>
            <p className="text-lg text-gray-600">
              From your Riskalyze days: 4.8 meetings per day. 70% of your time.
              <br />
              The tyranny of the urgent crowding out the important.
              <br />
              Now at ContioAI, you're fixing meetings that matter. We eliminate the ones that shouldn't exist.
            </p>
          </div>

          <div className="mt-8 mb-8 p-6 bg-gray-50 rounded-xl border-l-4 border-[#45B787]">
            <p className="text-lg text-gray-800 italic mb-2">
              "Seven robots join the call, and do nothing to improve the meeting; they just record the misery."
            </p>
            <p className="text-sm text-gray-600">You on current AI notetakers</p>
            <p className="text-md text-gray-700 mt-3">
              That's why you built ContioAI differently. And why we built DoubleMe to eliminate those meetings entirely.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="p-6 bg-red-50 rounded-xl border-2 border-red-100">
              <div className="text-3xl font-bold text-red-600 mb-2">23 hrs/week</div>
              <div className="text-sm text-gray-700">wasted in unproductive calls and meetings</div>
            </div>
            <div className="p-6 bg-red-50 rounded-xl border-2 border-red-100">
              <div className="text-3xl font-bold text-red-600 mb-2">67%</div>
              <div className="text-sm text-gray-700">of meetings could be handled async or eliminated</div>
            </div>
            <div className="p-6 bg-red-50 rounded-xl border-2 border-red-100">
              <div className="text-3xl font-bold text-red-600 mb-2">Friday 4:30 PM</div>
              <div className="text-sm text-gray-700">meeting = hostage situation (your words!)</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#45B787]/10 to-[#FFD23F]/10 p-8 rounded-2xl border-2 border-[#45B787]/20">
            <p className="text-lg text-gray-800 italic mb-2">
              "The best leaders don't manage time; they architect outcomes."
            </p>
            <p className="text-sm text-gray-600">Aaron Klein, aaronklein.com</p>
          </div>
        </div>
      </div>

      {/* The Solution */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Perfect Complement to ContioAI
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              While Contio makes your <span className="font-semibold">necessary meetings brilliant</span>,
              DoubleMe <span className="font-semibold">eliminates the unnecessary ones entirely</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-[#45B787]/20">
              <div className="w-12 h-12 bg-[#45B787] rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Phone Calls</h3>
              <p className="text-gray-600 mb-4">
                Your AI double handles inbound calls, qualifies leads, answers FAQs, and schedules
                meetings, all with natural conversation that sounds like you.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#45B787] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">24/7 availability without burning out</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#45B787] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Handles routine check-ins automatically</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#45B787] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Only escalates what truly needs you</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-[#FFD23F]/20">
              <div className="w-12 h-12 bg-[#FFD23F] rounded-xl flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-gray-900" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Call Management</h3>
              <p className="text-gray-600 mb-4">
                Your double understands context from past conversations, captures every detail,
                and creates actionable follow-ups so nothing falls through the cracks.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#FFD23F] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Auto-generates call summaries and follow-up tasks</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#FFD23F] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Remembers every conversation detail</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#FFD23F] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Keeps your relationships warm on autopilot</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#45B787] to-[#3a9970] p-8 rounded-2xl text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">The Power Combo</h3>
                <p className="text-white/90 text-lg">
                  Use ContioAI for your strategic meetings with investors and key partners.
                  Use DoubleMe to handle everything else: prospect calls, routine check-ins,
                  cross-company collaboration, and follow-ups. Reclaim 15+ hours per week.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in 15 Minutes
            </h2>
            <p className="text-lg text-gray-600">
              (We'll handle the phone number setup for you)
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="absolute -left-4 -top-4 w-12 h-12 bg-[#45B787] rounded-full flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
              <div className="bg-gray-50 p-8 rounded-xl border-2 border-gray-200 pt-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Create Your Double</h3>
                <p className="text-gray-600">
                  Give your AI assistant a name, voice, and personality.
                  Train it on your FAQs, talking points, and how you like to communicate.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-4 -top-4 w-12 h-12 bg-[#FFD23F] rounded-full flex items-center justify-center text-gray-900 font-bold text-xl">
                2
              </div>
              <div className="bg-gray-50 p-8 rounded-xl border-2 border-gray-200 pt-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Connect Your Phone</h3>
                <p className="text-gray-600">
                  Link your business number (or get a new one).
                  Set your availability and preferences. Your double handles the rest.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-4 -top-4 w-12 h-12 bg-[#FF9B50] rounded-full flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
              <div className="bg-gray-50 p-8 rounded-xl border-2 border-gray-200 pt-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Let It Run</h3>
                <p className="text-gray-600">
                  Your AI double takes calls, qualifies leads, schedules meetings,
                  and sends you summaries. You only jump in when truly needed.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-8 py-4 bg-[#45B787] text-white rounded-lg font-semibold hover:bg-[#3a9970] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get on the Beta List
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Use Cases for ContioAI Team */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for Teams Like Yours
            </h2>
            <p className="text-lg text-gray-600">
              Every role at ContioAI benefits from DoubleMe
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-[#45B787] transition-colors">
              <Users className="w-8 h-8 text-[#45B787] mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">For Aaron & Leadership</h3>
              <p className="text-gray-600 text-sm">
                Handle investor update calls, advisor network check-ins, and partnership
                discussions. Let your double do the first pass, send you summaries,
                and only pull you in for final decisions. More time with Ali and the kids.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-[#45B787] transition-colors">
              <Clock className="w-8 h-8 text-[#FFD23F] mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">For Engineering (Adam & Tom)</h3>
              <p className="text-gray-600 text-sm">
                Protect deep work time. Let DoubleMe handle vendor calls,
                customer support escalations, and scheduling. Focus on shipping features,
                not playing phone tag.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-[#45B787] transition-colors">
              <Calendar className="w-8 h-8 text-[#FF9B50] mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">For Operations (Andrew & Angie)</h3>
              <p className="text-gray-600 text-sm">
                Automate onboarding calls, customer success check-ins, and follow-up
                scheduling. Keep the machine running smooth while you focus on strategy.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-[#45B787] transition-colors">
              <Sparkles className="w-8 h-8 text-[#45B787] mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">For Your Investor Network</h3>
              <p className="text-gray-600 text-sm">
                Your seed investors (Eric Clarke, Steve Lockshin, Ric Edelman) built the advisor tech stack.
                Their networks field 40+ "quick question" calls weekly. DoubleMe handles these routine calls
                while advisors focus on $50K planning engagements. Bundle with ContioAI and own the productivity layer.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 bg-gradient-to-r from-[#45B787] to-[#3a9970]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Own the Entire Advisor Stack?
          </h2>
          <p className="text-xl text-white/90 mb-4">
            When ContioAI takes off, the calls will flood in. Reclaim 23 hours weekly before it becomes a bottleneck.
          </p>
          <p className="text-lg text-white/80 mb-8">
            We'll work with you to <span className="font-bold">custom build your doubles</span>, tailored
            to your team's voice, workflows, and needs. Direct access to our founders.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-8 py-4 bg-white text-[#45B787] rounded-lg font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get on the Beta List
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <a
              href="mailto:six@getadouble.com?subject=Aaron%20-%20Strategic%20Partnership%20for%20Q1%20Launch"
              className="inline-flex items-center px-8 py-4 bg-transparent text-white border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-all"
            >
              Let's Talk Partnership
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#2C3E50] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-400 text-sm">
            <p>© 2025 DoubleMe. Built for busy humans who value freedom.</p>
            <p className="mt-2">
              Questions? Call us at{' '}
              <a href="tel:+16156901805" className="text-[#45B787] hover:text-[#FFD23F]">
                +1 (615) 690-1805
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
